import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import { pushUnique, type CustomerUserApiKey, type ObjectFilter } from '@talos/kyoko';
import {
  accountPermissionsToObjectFilters,
  areObjectFiltersEqual,
  objectFiltersToAccountPermissions,
  type ApiKeyAccountPermissions,
} from '../../CustomerApiKeys/objectFilters';
import { initCustomerUser } from '../userDetails/userDetailsSlice';

function getInitialState() {
  return {
    // setting as undefined instead of [] in order to display the loading spinner in ag-grid
    keys: undefined as CustomerUserApiKey[] | undefined,
    dirtyKeys: [] as string[],
    keysToDelete: [] as string[],
    fetching: false,
    selectedApiKeyId: undefined as string | undefined,
    /** Last persisted ObjectFilters per API key, refreshed on every successful save. Edits are diffed against these. */
    savedObjectFiltersByKeyId: {} as Record<string, ObjectFilter[]>,
    /** Last persisted WhitelistedIps per API key, refreshed on every successful save. */
    savedWhitelistedIpsByKeyId: {} as Record<string, string>,
    /** Last persisted Label per API key, refreshed on every successful save. */
    savedLabelsByKeyId: {} as Record<string, string>,
    /** Soft-deleted account names per API key; rows stay visible (Removed) until save succeeds. */
    deletedAccountsByKeyId: {} as Record<string, string[]>,
  };
}

type ApiKeysState = ReturnType<typeof getInitialState>;

function normalizeApiKey(apiKey: CustomerUserApiKey): CustomerUserApiKey {
  return {
    ...apiKey,
    WhitelistedIps: apiKey.WhitelistedIps ?? '',
    ObjectFilters: apiKey.ObjectFilters ?? [],
  };
}

function getKey(keys: CustomerUserApiKey[] | undefined, customerUserApiKeyId: string) {
  return keys?.find(key => key.CustomerUserApiKeyID === customerUserApiKeyId);
}

function getDeletedAccounts(state: ApiKeysState, apiKeyId: string): string[] {
  return state.deletedAccountsByKeyId[apiKeyId] ?? [];
}

/** ObjectFilters excluding soft-deleted accounts — what will be persisted on save. */
export function getEffectiveObjectFilters(state: ApiKeysState, apiKeyId: string): ObjectFilter[] {
  const filters = getKey(state.keys, apiKeyId)?.ObjectFilters ?? [];
  const deleted = getDeletedAccounts(state, apiKeyId);
  if (deleted.length === 0) {
    return filters;
  }
  return filters.filter(filter => !deleted.includes(filter.MarketAccount));
}

export function hasWhitelistedIpsChanged(state: ApiKeysState, apiKeyId: string): boolean {
  return (state.savedWhitelistedIpsByKeyId[apiKeyId] ?? '') !== (getKey(state.keys, apiKeyId)?.WhitelistedIps ?? '');
}

export function hasLabelChanged(state: ApiKeysState, apiKeyId: string): boolean {
  return (state.savedLabelsByKeyId[apiKeyId] ?? '') !== (getKey(state.keys, apiKeyId)?.Label ?? '');
}

export function hasApiKeyDetailsChanged(state: ApiKeysState, apiKeyId: string): boolean {
  return hasWhitelistedIpsChanged(state, apiKeyId) || hasLabelChanged(state, apiKeyId);
}

export function hasObjectFiltersChanged(state: ApiKeysState, apiKeyId: string): boolean {
  return !areObjectFiltersEqual(state.savedObjectFiltersByKeyId[apiKeyId], getEffectiveObjectFilters(state, apiKeyId));
}

function syncDirtyKeys(state: ApiKeysState, apiKeyId: string) {
  if (hasObjectFiltersChanged(state, apiKeyId) || hasApiKeyDetailsChanged(state, apiKeyId)) {
    state.dirtyKeys = pushUnique(state.dirtyKeys, apiKeyId);
  } else {
    state.dirtyKeys = state.dirtyKeys.filter(id => id !== apiKeyId);
  }
}

const apiKeysSlice = createSlice({
  name: 'apiKeys',
  initialState: getInitialState(),
  reducers: {
    fetchingKeys: state => {
      state.fetching = true;
    },
    setApiKeys: (state, { payload: apiKeys }: PayloadAction<CustomerUserApiKey[]>) => {
      const normalized = apiKeys.map(normalizeApiKey);
      state.keys = normalized;
      state.fetching = false;
      state.savedObjectFiltersByKeyId = Object.fromEntries(
        normalized.map(key => [key.CustomerUserApiKeyID, [...(key.ObjectFilters ?? [])]])
      );
      state.savedWhitelistedIpsByKeyId = Object.fromEntries(
        normalized.map(key => [key.CustomerUserApiKeyID, key.WhitelistedIps ?? ''])
      );
      state.savedLabelsByKeyId = Object.fromEntries(normalized.map(key => [key.CustomerUserApiKeyID, key.Label ?? '']));
      state.deletedAccountsByKeyId = {};
      state.selectedApiKeyId = normalized.at(0)?.CustomerUserApiKeyID;
    },
    addApiKey: (state, { payload: apiKey }: PayloadAction<CustomerUserApiKey>) => {
      const normalized = normalizeApiKey(apiKey);
      state.keys?.push(normalized);
      state.savedObjectFiltersByKeyId[normalized.CustomerUserApiKeyID] = [...(normalized.ObjectFilters ?? [])];
      state.savedWhitelistedIpsByKeyId[normalized.CustomerUserApiKeyID] = normalized.WhitelistedIps ?? '';
      state.savedLabelsByKeyId[normalized.CustomerUserApiKeyID] = normalized.Label ?? '';
      state.selectedApiKeyId = normalized.CustomerUserApiKeyID;
    },
    selectApiKey: (state, { payload: apiKeyId }: PayloadAction<string>) => {
      state.selectedApiKeyId = apiKeyId;
    },
    updateApiKey: (state, { payload }: PayloadAction<CustomerUserApiKey>) => {
      const keyToUpdate = getKey(state.keys, payload.CustomerUserApiKeyID);
      if (keyToUpdate) {
        keyToUpdate.WhitelistedIps = payload.WhitelistedIps;
        keyToUpdate.Label = payload.Label;
        syncDirtyKeys(state, payload.CustomerUserApiKeyID);
      }
    },
    setApiKeyAccountPermissions: (
      state,
      { payload }: PayloadAction<{ apiKeyId: string; accountPermissions: ApiKeyAccountPermissions[] }>
    ) => {
      const keyToUpdate = getKey(state.keys, payload.apiKeyId);
      if (!keyToUpdate) {
        return;
      }
      keyToUpdate.ObjectFilters = accountPermissionsToObjectFilters(payload.accountPermissions);
      // Re-adding an account clears a pending soft-delete for that account.
      const deleted = getDeletedAccounts(state, payload.apiKeyId);
      if (deleted.length > 0) {
        const nextAccounts = new Set(payload.accountPermissions.map(account => account.customerAccount));
        state.deletedAccountsByKeyId[payload.apiKeyId] = deleted.filter(account => !nextAccounts.has(account));
      }
      syncDirtyKeys(state, payload.apiKeyId);
    },
    deleteApiKeyAccount: (state, { payload }: PayloadAction<{ apiKeyId: string; customerAccount: string }>) => {
      const keyToUpdate = getKey(state.keys, payload.apiKeyId);
      if (!keyToUpdate) {
        return;
      }
      const savedAccounts = new Set(
        objectFiltersToAccountPermissions(state.savedObjectFiltersByKeyId[payload.apiKeyId]).map(
          account => account.customerAccount
        )
      );

      if (savedAccounts.has(payload.customerAccount)) {
        state.deletedAccountsByKeyId[payload.apiKeyId] = pushUnique(
          getDeletedAccounts(state, payload.apiKeyId),
          payload.customerAccount
        );
      } else {
        // Row was added in this editing session: remove it immediately (same as Account Restrictions).
        keyToUpdate.ObjectFilters = (keyToUpdate.ObjectFilters ?? []).filter(
          filter => filter.MarketAccount !== payload.customerAccount
        );
      }
      syncDirtyKeys(state, payload.apiKeyId);
    },
    deleteApiKey: (state, { payload }: PayloadAction<CustomerUserApiKey>) => {
      state.keysToDelete = pushUnique(state.keysToDelete, payload.CustomerUserApiKeyID);
      if (state.selectedApiKeyId === payload.CustomerUserApiKeyID) {
        state.selectedApiKeyId = state.keys
          ?.filter(key => !state.keysToDelete.includes(key.CustomerUserApiKeyID))
          .at(0)?.CustomerUserApiKeyID;
      }
    },
    apiKeySaved: (state, { payload: apiKeyId }: PayloadAction<string>) => {
      state.dirtyKeys = state.dirtyKeys.filter(key => key !== apiKeyId);
      const key = getKey(state.keys, apiKeyId);
      if (key) {
        const effective = getEffectiveObjectFilters(state, apiKeyId);
        key.ObjectFilters = effective;
        state.savedObjectFiltersByKeyId[apiKeyId] = [...effective];
        state.savedWhitelistedIpsByKeyId[apiKeyId] = key.WhitelistedIps ?? '';
        state.savedLabelsByKeyId[apiKeyId] = key.Label ?? '';
      }
      delete state.deletedAccountsByKeyId[apiKeyId];
    },
    apiKeyDeleted: (state, { payload: apiKeyId }: PayloadAction<string>) => {
      // Keep local state consistent with what succeeded so the open drawer matches the saved result
      // (partial failures leave the drawer open for retry).
      state.keysToDelete = state.keysToDelete.filter(key => key !== apiKeyId);
      state.keys = state.keys?.filter(key => key.CustomerUserApiKeyID !== apiKeyId);
      delete state.savedObjectFiltersByKeyId[apiKeyId];
      delete state.savedWhitelistedIpsByKeyId[apiKeyId];
      delete state.savedLabelsByKeyId[apiKeyId];
      delete state.deletedAccountsByKeyId[apiKeyId];
      if (state.selectedApiKeyId === apiKeyId) {
        state.selectedApiKeyId = state.keys?.at(0)?.CustomerUserApiKeyID;
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(initCustomerUser, _ => {
      return getInitialState();
    });
  },
});

export function isApiKeysTabDirty(state: ApiKeysState) {
  return state.keysToDelete.length > 0 || state.dirtyKeys.some(apiKeyId => hasApiKeyDetailsChanged(state, apiKeyId));
}

export function isApiKeyAccountFiltersTabDirty(state: ApiKeysState) {
  return state.dirtyKeys.some(apiKeyId => hasObjectFiltersChanged(state, apiKeyId));
}

/** Keys whose Label or WhitelistedIps changed — the edits the API Keys tab marks as dirty. */
export const selectDirtyApiKeyRowIds = createSelector(
  (state: AppState) => state.customerUserDrawer.apiKeys,
  apiKeysState => apiKeysState.dirtyKeys.filter(apiKeyId => hasApiKeyDetailsChanged(apiKeysState, apiKeyId))
);

export const {
  setApiKeys,
  addApiKey,
  selectApiKey,
  updateApiKey,
  setApiKeyAccountPermissions,
  deleteApiKeyAccount,
  deleteApiKey,
  fetchingKeys,
  apiKeySaved,
  apiKeyDeleted,
} = apiKeysSlice.actions;
export const apiKeysSliceReducer = apiKeysSlice.reducer;
